import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnDestroy, OnInit, TemplateRef, ViewChild, } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import Swal from 'sweetalert2';
import { NgxPagerComponent } from '../../shared/components/ngx-pager/ngx-pager.component';
import { PageResultCounterComponent } from '../../shared/components/ngx-pager/pageresult-counter/pageresult-counter.component';
import { InvoiceStatusEnum } from '../../shared/enums/invoice-status.enum';
import { SmartLabelEnum } from '../../shared/enums/smartlabel.enum';
import { Attendee } from '../../shared/models/attendee.model';
import { Event } from '../../shared/models/event.model';
import { PageResult } from '../../shared/models/page-result.model';
import { OfflineService } from '../../shared/services/offline.service';
import { RegistryApiService } from '../../shared/services/registry-api.service';
import { AttendanceForm } from '../attendance-form/attendance-form.form';
import { AttendanceVerifyModalComponent } from '../attendance-verify-modal/attendance-verify-modal.component';
import { AttendanceListRecordComponent } from './attendance-list-record/attendance-list-record.component';
import { SmartLabelService } from '../../shared/services/smartlabel.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'attendance-list',
    standalone: true,
    providers: [DecimalPipe, BsModalService, RegistryApiService],
    imports: [
        CommonModule,
        PageResultCounterComponent,
        NgxPagerComponent,
        AttendanceListRecordComponent,
        PageResultCounterComponent,
        AttendanceVerifyModalComponent,
    ],
    templateUrl: './attendance-list.component.html',
})
export class AttendanceListComponent implements OnInit, OnDestroy {
    @Input({ required: true }) public event!: Event; // Define an verifyAttendeeModalRef property to receive the event ID

    @Input({ required: true }) public attendanceForm!: AttendanceForm;

    @Input({ required: true }) public refresh!: Subject<PageResult<Attendee>>;

    @Input({ required: true }) public verifyAttendeeEvent!: Subject<Attendee>;

    @Input({ required: true }) isOnline: boolean = true;

    @ViewChild('verifyModalref', { read: TemplateRef })

    private readonly destroyRef = inject(DestroyRef);
    private readonly registryApiService = inject(RegistryApiService);
    private readonly smartLabelService = inject(SmartLabelService);

    /** TODO: Unexpected any. Specify a different type. */
    public verifyAttendeeModal!: TemplateRef<any>;

    public showList: boolean = true;

    public verifyingAttendee: Attendee = new Attendee();

    public registryIdLabel: string = 'N/A';

    private currentModal?: BsModalRef;

    private readonly modalConfig = {
        animated: true,
        backdrop: 'static' as const,
        keyboard: false,
    };

    protected readonly smartLabels = {
        [SmartLabelEnum.RegistryIDLabel]: '',
    };

    constructor(
        private readonly modalService: BsModalService,
        private readonly offlineService: OfflineService,
    ) { }

    protected get canVerifyAttendees(): boolean {
        return (
            !!this.event &&
            this.event.editRoster &&
            !!this.event.startDate &&
            this.convertToDateOnly(this.event.startDate) <=
            this.convertToDateOnly(new Date())
        );
    }

    /** Initializes smartlabel values with fallbacks. */
    private readonly initSmartLabels = (): void => {
        /** Re-type the entries so TypeScript treats each key as a valid smartlabels key. */
        const smartLabelEntries = Object.entries(this.smartLabels) as Array<[keyof typeof this.smartLabels, string]>;

        /** Load each smartlabel from the service and fall back to its default text when needed. */
        for (const [labelName, defaultText] of smartLabelEntries) {
            this.smartLabelService
                .getLabelText(labelName, { fallback: defaultText })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((translatedText: string) => this.smartLabels[labelName] = translatedText);
        }
    };

    /** Performs synchronous initialization logic. */
    private readonly onInitSync = (): void => {
        this.initSmartLabels();

        this.refresh.subscribe((attendeeList: PageResult<Attendee>) => {
            this.refreshListDataAfterAdd(attendeeList);
        });

        this.verifyAttendeeEvent.subscribe((attendee: Attendee) => {
            this.showVerifyModal({
                attendee: attendee,
                modalRef: this.verifyAttendeeModal,
            });
        });
    };

    /** Performs asynchronous initialization logic. */
    private readonly onInitAsync = async (): Promise<void> => {
        await this.attendanceForm?.reload(true);

        // Check if there are offline registered users for the event and if so, flag them as registered already
        const offlineRegistered =
            this.offlineService.getOfflineUsersForEvent(this.event.id);

        offlineRegistered?.forEach((attendeeId) => {
            const _foundAttendee = this.attendanceForm.resultModels.find(
                (x) => x.appId === attendeeId,
            );
            if (_foundAttendee) {
                _foundAttendee.attended = true;
            }
        });
    };

    // Initialize the component
    ngOnInit(): void {
        this.onInitSync();
        this.onInitAsync().finally(() => {
            /** Filler for when the async calls are done. */
        });
    }

    ngOnDestroy(): void {
        this.refresh.unsubscribe();
        this.verifyAttendeeEvent.unsubscribe();
    }

    protected get showAlert(): boolean {
        return this.attendanceForm.totalCount === 0;
    }

    protected get showResultsLabel(): boolean {
        return this.attendanceForm.totalCount > 0;
    }

    private readonly paymentStatusText = (attendee: Attendee): string => {
        let paymentStatus: boolean | undefined;
        if (attendee?.registrationInvoiceId !== null) {
            paymentStatus =
                attendee?.registrationInvoiceStatusId ===
                InvoiceStatusEnum.Paid;
        }
        
        if (paymentStatus === undefined) {
            return '';
        } else if (paymentStatus) {
            return '<br>Payment Status: Paid';
        } else {
            return '<br>Payment Status: Not Paid';
        }
    };

    // This is called when the refresh observable is called
    private readonly refreshListDataAfterAdd = (
        attendeeList: PageResult<Attendee>,
    ): void => {
        this.showList = false;
        this.attendanceForm.resultModels = attendeeList.data;
        this.attendanceForm.totalCount = attendeeList.totalCount;
        this.showList = true;
        Swal.fire({
            title: 'Success',
            text: 'The Attendee has been added!',
            icon: 'success',
            confirmButtonText: 'Ok',
        });
    };

    protected verifyAttendee = async (attendee: Attendee): Promise<void> => {
        try {
            const result: boolean = await this.registryApiService.verifyAttendee(
                attendee.appId,
                this.event.id,
            );

            if (!result || !this.attendanceForm.resultModels) {
                this.displayError();
                return;
            }

            const matchedAttendee: Attendee | undefined = this.attendanceForm.resultModels.find(
                (curAttendee: Attendee) => curAttendee.appId === attendee.appId,
            );

            if (matchedAttendee) {
                matchedAttendee.attended = true;
            }

            await Swal.fire({
                title: 'Attendance Verified',
                html: `
                The following attendee was verified:
                <br>${this.registryIdLabel} #${attendee.appId || 'N/A'}
                <br>${attendee.firstName} ${attendee.lastName}
                <br>Email: ${attendee.email || 'N/A'}
                ${this.paymentStatusText(attendee)}
            `,
                icon: 'success',
                confirmButtonText: 'Ok',
            });

            this.exitModal();
        } catch {
            if (!this.isOnline) {
                // Store offline event to be processed later
                this.offlineService.setOfflineUserForEvent({
                    eventId: this.event.id,
                    userId: attendee.appId,
                });

                // Flag attendee as attended and close modal
                attendee.attended = true;

                await Swal.fire({
                    title: 'Offline Attendance Verified',
                    html: `
                    The following attendee was verified and will process when connection is re-established:
                    <br>${this.registryIdLabel} #${attendee.appId || 'N/A'}
                    <br>${attendee.firstName} ${attendee.lastName}
                    <br>Email: ${attendee.email || 'N/A'}
                    ${this.paymentStatusText(attendee)}
                `,
                    icon: 'warning',
                    confirmButtonText: 'Ok',
                });

                this.exitModal();
                return;
            }

            this.displayError();
        }
    };

    private readonly displayError = (): void => {
        Swal.fire({
            title: 'ERROR',
            text: 'The Attendee could Not be verified!',
            icon: 'error',
            confirmButtonText: 'Ok',
        });
        this.exitModal();
    };

    private readonly convertToDateOnly = (date: string | Date): Date => {
        const dateValue = new Date(date);

        const dateWithNoTimezone = new Date(
            Date.UTC(
                dateValue.getUTCFullYear(),
                dateValue.getUTCMonth(),
                dateValue.getUTCDate(),
            ),
        );

        return dateWithNoTimezone;
    };

    protected exitModal = (): void => {
        this.currentModal?.hide();
    };

    protected showVerifyModal = (data: {
        attendee: Attendee;
        modalRef: TemplateRef<any>;
    }): void => {
        this.verifyingAttendee = data.attendee;
        this.openModal(data.modalRef);
    };

    protected openModal = (modalTemplate: TemplateRef<any>): void => {
        this.currentModal = this.modalService.show(
            modalTemplate,
            this.modalConfig,
        );
    };
}
